import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/communitySupport.css";

const API_BASE_URL = "https://naxor.naxora.net";

const CommunitySupport = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("ai");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [aiInput, setAiInput] = useState("");
  const [aiMessages, setAiMessages] = useState([]);
  const [communityInput, setCommunityInput] = useState("");
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketReplyInputs, setTicketReplyInputs] = useState({});
  const [permission, setPermission] = useState(null);
  const [leaderStatus, setLeaderStatus] = useState(null);
  const [communityMessages, setCommunityMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tickets, setTickets] = useState([]);

  const token = useMemo(() => {
    return localStorage.getItem("token") || localStorage.getItem("naxoraToken");
  }, []);

  const authHeaders = useMemo(() => {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [token]);

  const showMessage = (text) => {
    setMessage(text || "");
    if (text) {
      setTimeout(() => {
        setMessage("");
      }, 3500);
    }
  };

  const handleUnauthorized = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("naxoraToken");
    navigate("/login");
  };

  const requestApi = async (endpoint, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...authHeaders,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401) {
      handleUnauthorized();
      return null;
    }

    if (!response.ok) {
      throw new Error(data.message || "Request failed");
    }

    return data;
  };

  const loadCommunityData = async () => {
    try {
      setLoading(true);

      const [
        permissionData,
        leaderData,
        messagesData,
        announcementsData,
        ticketsData,
      ] = await Promise.all([
        requestApi("/api/community/my-permission"),
        requestApi("/api/community/leader-status"),
        requestApi("/api/community/messages"),
        requestApi("/api/community/announcements"),
        requestApi("/api/support/my-tickets"),
      ]);

      if (permissionData?.permission) {
        setPermission(permissionData.permission);
      }

      if (leaderData?.leaderStatus) {
        setLeaderStatus(leaderData.leaderStatus);
      }

      if (Array.isArray(messagesData?.messages)) {
        setCommunityMessages(messagesData.messages);
      }

      if (Array.isArray(announcementsData?.announcements)) {
        setAnnouncements(announcementsData.announcements);
      }

      if (Array.isArray(ticketsData?.tickets)) {
        setTickets(ticketsData.tickets);
      }
    } catch (error) {
      showMessage(error.message || "Failed to load community data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    loadCommunityData();
  }, [token]);

  const sendAiMessage = async (e) => {
    e.preventDefault();

    const cleanMessage = aiInput.trim();

    if (!cleanMessage) {
      showMessage("Please enter your message");
      return;
    }

    try {
      setActionLoading(true);

      const userMessage = {
        sender: "user",
        message: cleanMessage,
        createdAt: new Date().toISOString(),
      };

      setAiMessages((prev) => [...prev, userMessage]);
      setAiInput("");

      const data = await requestApi("/api/support/ai-message", {
        method: "POST",
        body: JSON.stringify({
          message: cleanMessage,
        }),
      });

      if (data?.aiReply) {
        setAiMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            message: data.aiReply,
            createdAt: new Date().toISOString(),
          },
        ]);
      }

      if (data?.ticket) {
        setTickets((prev) => [data.ticket, ...prev]);
      }

      if (data?.needsSeniorAdmin) {
        showMessage("Your issue has been forwarded to Senior Admin review");
      }
    } catch (error) {
      showMessage(error.message || "Failed to send AI message");
    } finally {
      setActionLoading(false);
    }
  };

  const sendCommunityMessage = async (e) => {
    e.preventDefault();

    const cleanMessage = communityInput.trim();

    if (!cleanMessage) {
      showMessage("Please enter your community message");
      return;
    }

    try {
      setActionLoading(true);

      const data = await requestApi("/api/community/messages", {
        method: "POST",
        body: JSON.stringify({
          message: cleanMessage,
        }),
      });

      if (data?.communityMessage) {
        setCommunityMessages((prev) => [...prev, data.communityMessage]);
        setCommunityInput("");
        showMessage("Community message sent successfully");
      }
    } catch (error) {
      showMessage(error.message || "Failed to send community message");
    } finally {
      setActionLoading(false);
    }
  };

  const createTicket = async (e) => {
    e.preventDefault();

    const cleanSubject = ticketSubject.trim();
    const cleanMessage = ticketMessage.trim();

    if (!cleanMessage) {
      showMessage("Please enter your support message");
      return;
    }

    try {
      setActionLoading(true);

      const data = await requestApi("/api/support/ticket", {
        method: "POST",
        body: JSON.stringify({
          subject: cleanSubject || "Support Request",
          message: cleanMessage,
        }),
      });

      if (data?.ticket) {
        setTickets((prev) => [data.ticket, ...prev]);
        setTicketSubject("");
        setTicketMessage("");
        showMessage("Support ticket created successfully");
      }
    } catch (error) {
      showMessage(error.message || "Failed to create support ticket");
    } finally {
      setActionLoading(false);
    }
  };

  const addTicketReply = async (ticketId) => {
    const cleanMessage = String(ticketReplyInputs[ticketId] || "").trim();

    if (!cleanMessage) {
      showMessage("Please enter your reply");
      return;
    }

    try {
      setActionLoading(true);

      const data = await requestApi(`/api/support/ticket/${ticketId}/message`, {
        method: "POST",
        body: JSON.stringify({
          message: cleanMessage,
        }),
      });

      if (data?.ticket) {
        setTickets((prev) =>
          prev.map((ticket) =>
            String(ticket.id) === String(ticketId) ? data.ticket : ticket
          )
        );

        setTicketReplyInputs((prev) => ({
          ...prev,
          [ticketId]: "",
        }));

        showMessage("Reply added successfully");
      }
    } catch (error) {
      showMessage(error.message || "Failed to add reply");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusLabel = (status) => {
    const value = String(status || "").replace(/_/g, " ");

    if (!value) return "Open";

    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const renderHeader = () => {
    return (
      <div className="community-header">
        <button className="community-back-btn" onClick={() => navigate("/dashboard")}>
          ←
        </button>

        <div>
          <h1>NAXORA Community Hub</h1>
          <p>AI support, leader community, official updates, and support tickets.</p>
        </div>
      </div>
    );
  };

  const renderTabs = () => {
    return (
      <div className="community-tabs">
        <button
          className={activeTab === "ai" ? "active" : ""}
          onClick={() => setActiveTab("ai")}
        >
          AI Support
        </button>

        <button
          className={activeTab === "community" ? "active" : ""}
          onClick={() => setActiveTab("community")}
        >
          Community
        </button>

        <button
          className={activeTab === "leader" ? "active" : ""}
          onClick={() => setActiveTab("leader")}
        >
          Leader Zone
        </button>

        <button
          className={activeTab === "announcements" ? "active" : ""}
          onClick={() => setActiveTab("announcements")}
        >
          Announcements
        </button>

        <button
          className={activeTab === "tickets" ? "active" : ""}
          onClick={() => setActiveTab("tickets")}
        >
          Tickets
        </button>
      </div>
    );
  };

  const renderAiSupport = () => {
    return (
      <div className="community-card">
        <div className="section-title">
          <h2>AI Support</h2>
          <p>Ask about withdrawals, deposits, referral commission, salary, strategy, KYC, or account issues.</p>
        </div>

        <div className="ai-info-box">
          <span>Get guidance about deposits and withdrawals</span>
          <span>Understand your account and strategy status</span>
          <span>Learn about referral and leader benefits</span>
          <span>Report issues for Senior Admin review</span>
        </div>

        <div className="chat-box">
          {aiMessages.length === 0 ? (
            <div className="empty-state">
              Start a conversation with NAXORA AI Support.
            </div>
          ) : (
            aiMessages.map((item, index) => (
              <div
                key={`${item.createdAt}_${index}`}
                className={`chat-message ${item.sender === "user" ? "user" : "ai"}`}
              >
                <div className="chat-bubble">
                  <span className="chat-sender">
                    {item.sender === "user" ? "You" : "NAXORA AI"}
                  </span>
                  <p>{item.message}</p>
                  <small>{formatDate(item.createdAt)}</small>
                </div>
              </div>
            ))
          )}
        </div>

        <form className="message-form" onSubmit={sendAiMessage}>
          <input
            type="text"
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
            placeholder="Type your support question..."
            maxLength={500}
          />
          <button type="submit" disabled={actionLoading}>
            Send
          </button>
        </form>
      </div>
    );
  };

  const renderCommunity = () => {
    const canSend = permission?.canSend === true;

    return (
      <div className="community-card">
        <div className="section-title">
          <h2>Community Chat</h2>
          <p>Read official community discussions. Message access is available for qualified leaders only.</p>
        </div>

        <div className={canSend ? "access-box unlocked" : "access-box locked"}>
          <strong>{canSend ? "Message Access Active" : "Message Access Locked"}</strong>
          <span>{permission?.message || "Checking your community permission..."}</span>
        </div>

        <div className="community-message-list">
          {communityMessages.length === 0 ? (
            <div className="empty-state">No community messages yet.</div>
          ) : (
            communityMessages.map((item) => (
              <div key={item.id} className="community-message-item">
                <div className="community-message-top">
                  <div>
                    <strong>{item.senderName}</strong>
                    <span>{item.senderBadge}</span>
                  </div>
                  <small>{formatDate(item.createdAt)}</small>
                </div>
                <p>{item.message}</p>
              </div>
            ))
          )}
        </div>

        <form className="message-form" onSubmit={sendCommunityMessage}>
          <input
            type="text"
            value={communityInput}
            onChange={(e) => setCommunityInput(e.target.value)}
            placeholder={
              canSend
                ? "Write a community message..."
                : "Community message permission is locked"
            }
            maxLength={500}
            disabled={!canSend}
          />
          <button type="submit" disabled={!canSend || actionLoading}>
            Send
          </button>
        </form>
      </div>
    );
  };

  const renderLeaderZone = () => {
    return (
      <div className="community-card">
        <div className="section-title">
          <h2>Leader Zone</h2>
          <p>Your leader access, community permission, and weekly salary status.</p>
        </div>

        <div className="leader-grid">
          <div className="leader-stat">
            <span>Required Active Members</span>
            <strong>{leaderStatus?.requiredActiveDirectMembers ?? 5}</strong>
          </div>

          <div className="leader-stat">
            <span>Your Active Members</span>
            <strong>{leaderStatus?.activeDirectMembers ?? 0}</strong>
          </div>

          <div className="leader-stat">
            <span>Remaining</span>
            <strong>{leaderStatus?.remainingActiveDirectMembers ?? 5}</strong>
          </div>

          <div className="leader-stat">
            <span>Weekly Salary</span>
            <strong>
              {leaderStatus?.weeklySalaryActive
                ? `$${leaderStatus?.weeklySalaryAmount || 0}`
                : "Locked"}
            </strong>
          </div>
        </div>

        <div
          className={
            leaderStatus?.communityChatUnlocked
              ? "access-box unlocked"
              : "access-box locked"
          }
        >
          <strong>
            {leaderStatus?.communityChatUnlocked
              ? "Leader Access Active"
              : "Leader Access Locked"}
          </strong>
          <span>
            {leaderStatus?.message ||
              "You need at least 5 active direct members to unlock leader access."}
          </span>
        </div>

        <div className="salary-levels">
          <h3>Weekly Salary Levels</h3>

          <div>
            <span>5 Active Members</span>
            <strong>$18 Weekly</strong>
          </div>

          <div>
            <span>10 Active Members</span>
            <strong>$38 Weekly</strong>
          </div>

          <div>
            <span>20 Active Members</span>
            <strong>$85 Weekly</strong>
          </div>

          <div>
            <span>50 Active Members</span>
            <strong>$230 Weekly</strong>
          </div>

          <div>
            <span>100 Active Members</span>
            <strong>$500 Weekly</strong>
          </div>
        </div>
      </div>
    );
  };

  const renderAnnouncements = () => {
    return (
      <div className="community-card">
        <div className="section-title">
          <h2>Announcements</h2>
          <p>Official NAXORA updates and important notices.</p>
        </div>

        <div className="announcement-list">
          {announcements.length === 0 ? (
            <div className="empty-state">No announcements available.</div>
          ) : (
            announcements.map((item) => (
              <div key={item.id} className={item.pinned ? "announcement pinned" : "announcement"}>
                <div className="announcement-top">
                  <h3>{item.title}</h3>
                  {item.pinned && <span>Pinned</span>}
                </div>
                <p>{item.message}</p>
                <small>{formatDate(item.createdAt)}</small>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderTickets = () => {
    return (
      <div className="community-card">
        <div className="section-title">
          <h2>My Support Tickets</h2>
          <p>Create a support ticket when your issue requires manual review.</p>
        </div>

        <form className="ticket-form" onSubmit={createTicket}>
          <input
            type="text"
            value={ticketSubject}
            onChange={(e) => setTicketSubject(e.target.value)}
            placeholder="Subject"
            maxLength={120}
          />

          <textarea
            value={ticketMessage}
            onChange={(e) => setTicketMessage(e.target.value)}
            placeholder="Describe your issue..."
            maxLength={1000}
          />

          <button type="submit" disabled={actionLoading}>
            Create Ticket
          </button>
        </form>

        <div className="ticket-list">
          {tickets.length === 0 ? (
            <div className="empty-state">No support tickets yet.</div>
          ) : (
            tickets.map((ticket) => (
              <div key={ticket.id} className="ticket-item">
                <div className="ticket-top">
                  <div>
                    <h3>{ticket.subject || "Support Request"}</h3>
                    <span>{getStatusLabel(ticket.status)}</span>
                  </div>
                  <small>{formatDate(ticket.createdAt)}</small>
                </div>

                {ticket.needsSeniorAdmin && (
                  <div className="review-badge">Senior Admin Review Required</div>
                )}

                <div className="ticket-messages">
                  {Array.isArray(ticket.messages) &&
                    ticket.messages.map((item) => (
                      <div key={item.id} className={`ticket-message ${item.sender}`}>
                        <strong>
                          {item.sender === "user"
                            ? "You"
                            : item.sender === "ai"
                            ? "NAXORA AI"
                            : "Admin"}
                        </strong>
                        <p>{item.message}</p>
                        <small>{formatDate(item.createdAt)}</small>
                      </div>
                    ))}
                </div>

                <div className="ticket-reply-box">
                  <input
                    type="text"
                    value={ticketReplyInputs[ticket.id] || ""}
                    onChange={(e) =>
                      setTicketReplyInputs((prev) => ({
                        ...prev,
                        [ticket.id]: e.target.value,
                      }))
                    }
                    placeholder="Add reply..."
                    maxLength={1000}
                  />
                  <button
                    type="button"
                    onClick={() => addTicketReply(ticket.id)}
                    disabled={actionLoading}
                  >
                    Reply
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderActiveTab = () => {
    if (activeTab === "ai") return renderAiSupport();
    if (activeTab === "community") return renderCommunity();
    if (activeTab === "leader") return renderLeaderZone();
    if (activeTab === "announcements") return renderAnnouncements();
    if (activeTab === "tickets") return renderTickets();

    return renderAiSupport();
  };

  return (
    <div className="community-page">
      {renderHeader()}

      {message && <div className="community-toast">{message}</div>}

      {renderTabs()}

      {loading ? (
        <div className="community-loading">Loading community hub...</div>
      ) : (
        renderActiveTab()
      )}
    </div>
  );
};

export default CommunitySupport;